import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router';
import styles from './responsive-lightbox.scss';
import MainItemNav from '../main-item-nav/main-item-nav';
import { ResponsiveVideoOverlay } from '../responsive-video-overlay/responsive-video-overlay';
import ResponsiveVideoInfo from '../responsive-video-info/responsive-video-info';
import ResponsiveHeader from '../responsive-header/responsive-header';
import ResponsiveVideoList from '../responsive-video-list/responsive-video-list';
import ResponsiveChannelInfo from '../responsive-channel-info/responsive-channel-info';
import Player from '../player/player';
import ShareOverlay from '../../../containers/share-overlay/share-overlay';
import { EXPANDED_THEME_VALUES } from '@wix/wix-vod-constants/app-settings';
import {
  canPlayFullOrPreview,
  getFullAccessActionName,
  FULL_ACCESS_ACTION_NAMES,
} from '@wix/wix-vod-shared/dist/esm/widget/ui-selectors/video-access';
import {
  VideoAspectRatio,
  Picture,
  PictureMode,
} from '@wix/wix-vod-shared/components';

import { getBuyButtonHandler } from '../../../utils/get-buy-button-handler';
import { getPlayButtonHandler } from '../../../utils/get-play-button-handler';
import VideoCover from '../../../components/video-cover/video-cover';

const BREAK_POINTS = [
  {
    min: 300,
    max: 768,
    width: 768,
    height: 432,
  },
  {
    min: 769,
    max: 1024,
    width: 1024,
    height: 576,
  },
  {
    min: 1025,
    width: 1920,
    height: 1080,
  },
];

export default withRouter(
  class ResponsiveLightbox extends React.Component {
    static propTypes = {
      currentSiteUser: PropTypes.object,
      mainVideo: PropTypes.object.isRequired,
      channel: PropTypes.object.isRequired,
      theme: PropTypes.oneOf([
        EXPANDED_THEME_VALUES.DARK,
        EXPANDED_THEME_VALUES.LIGHT,
      ]).isRequired,
      isChannelView: PropTypes.bool.isRequired,
      isMainActionsOnlyIcons: PropTypes.bool.isRequired,
      isRTL: PropTypes.bool.isRequired,
      isPlayerActive: PropTypes.bool,
      isVideoInfoVisible: PropTypes.bool,
      isVideoShareVisible: PropTypes.bool,
      isVideoListVisible: PropTypes.bool,
      PlayerComponent: PropTypes.func.isRequired,
      onPurchaseClick: PropTypes.func,
      onShareClick: PropTypes.func,
      onCloseClick: PropTypes.func,
      onPageRefresh: PropTypes.func,
      onAccountClick: PropTypes.func,
      onChannelClick: PropTypes.func,
      // InFrameActionCallbacks
      onClick: PropTypes.func,
      onPlayRequest: PropTypes.func,
      onPlayMemberOnlyRequest: PropTypes.func,
      onPlayWithoutPreviewRequest: PropTypes.func,
      onPurchaseRequest: PropTypes.func,
      onRentRequest: PropTypes.func,
      onSubscriptionRequest: PropTypes.func,
      // End of InFrameActionCallbacks
    };

    constructor(props) {
      super(props);

      this.videoListScrollHandler = null;
      this.lightboxRef = React.createRef();
    }

    componentDidMount() {
      this.lightboxRef.current.addEventListener(
        'scroll',
        this.videoListScrollHandler,
      );
    }

    componentWillUnmount() {
      this.lightboxRef.current.removeEventListener(
        'scroll',
        this.videoListScrollHandler,
      );
    }

    renderVideoView() {
      const {
        mainVideo,
        channel,
        currentSiteUser,
        theme,
        isMainActionsOnlyIcons,
        isRTL,
        isPlayerActive,
        isVideoShareVisible,
        isVideoInfoVisible,
        PlayerComponent,
        onPurchaseClick,
        onPlayRequest,
        onPlayMemberOnlyRequest,
        onSubscriptionRequest,
        onPurchaseRequest,
        onRentRequest,
      } = this.props;

      const fullAccessAction = getFullAccessActionName(
        channel,
        mainVideo,
        currentSiteUser,
      );

      const handlePlayClick = getPlayButtonHandler(
        onPlayRequest,
        onPlayMemberOnlyRequest,
        canPlayFullOrPreview(channel, mainVideo, currentSiteUser),
        fullAccessAction,
      );

      const handleBuyClick = getBuyButtonHandler(
        onSubscriptionRequest,
        onPurchaseRequest,
        onRentRequest,
        fullAccessAction,
      );

      return (
        <>
          <MainItemNav
            className={styles.nav}
            theme={theme}
            mainVideoId={mainVideo.id}
            isRTL={isRTL}
          />
          <div className={styles.content}>
            <div className={styles.mainItem}>
              <VideoAspectRatio>
                <Player
                  video={mainVideo}
                  PlayerComponent={PlayerComponent}
                  showPoster={false}
                  fillAllSpace
                />
                {!isPlayerActive && (
                  <div className={styles.mainItemOverlay}>
                    <VideoCover
                      videoItem={mainVideo}
                      breakpoints={BREAK_POINTS}
                      className={styles.mainItemCover}
                    />
                    <ResponsiveVideoOverlay
                      video={mainVideo}
                      channel={channel}
                      currentSiteUser={currentSiteUser}
                      showButtonsText={!isMainActionsOnlyIcons}
                      onPlayClick={handlePlayClick}
                      onBuyClick={handleBuyClick}
                    />
                  </div>
                )}
                <ShareOverlay
                  channelData={channel}
                  canShareVideo={isVideoShareVisible}
                  videoItem={mainVideo}
                />
              </VideoAspectRatio>
            </div>
            {isVideoInfoVisible && (
              <div className={styles.info}>
                <ResponsiveVideoInfo
                  video={mainVideo}
                  channel={channel}
                  currentSiteUser={currentSiteUser}
                  onPurchaseClick={
                    fullAccessAction === FULL_ACCESS_ACTION_NAMES.PRICING_PLAN
                      ? onSubscriptionRequest
                      : onPurchaseClick
                  }
                />
              </div>
            )}
          </div>
        </>
      );
    }

    renderChannelView() {
      const { channel, onPageRefresh } = this.props;
      const { customCoverUrl, coverUrl } = channel;

      return (
        <div className={styles.content}>
          <div className={styles.mainItem}>
            <VideoAspectRatio>
              <Picture
                className={styles.channelCover}
                src={customCoverUrl || coverUrl}
                mode={PictureMode.COVER}
                breakpoints={BREAK_POINTS}
              />
            </VideoAspectRatio>
          </div>
          <div className={styles.info}>
            <ResponsiveChannelInfo
              channel={channel}
              onPageRefresh={onPageRefresh}
            />
          </div>
        </div>
      );
    }

    navigateToVideoView = (videoId) => {
      const { history, channel } = this.props;
      history.push(`/channel/${channel.id}/video/${videoId}`);
    };

    handleBackButtonClick = () => {
      const { isChannelView, onCloseClick, onChannelClick } = this.props;
      isChannelView ? onCloseClick() : onChannelClick();
    };

    subscribeOnParentScroll = (handler) =>
      (this.videoListScrollHandler = handler);

    render() {
      const {
        mainVideo,
        channel,
        currentSiteUser,
        isChannelView,
        isRTL,
        isPlayerActive,
        isVideoInfoVisible,
        isVideoListVisible,
        onCloseClick,
        onShareClick,
        onPlayRequestedBi,
        onAccountClick,
        onPageRefresh,
        onChannelClick,
      } = this.props;

      return (
        <div
          ref={this.lightboxRef}
          data-hook="responsive-lightbox"
          className={classNames(styles.lightbox, {
            [styles.withInfo]: isVideoInfoVisible || isChannelView,
            [styles.withVideoList]: isVideoListVisible,
            [styles.channelView]: isChannelView,
            [styles.playerActive]: isPlayerActive && !isChannelView,
          })}
        >
          <div className={styles.headerContainer}>
            <ResponsiveHeader
              channel={channel}
              onCloseClick={onCloseClick}
              onShareClick={onShareClick}
              onAccountClick={onAccountClick}
              onPageRefresh={onPageRefresh}
              onChannelClick={onChannelClick}
              onBackButtonClick={this.handleBackButtonClick}
            />
          </div>
          <div className={styles.contentWrapper}>
            {isChannelView ? this.renderChannelView() : this.renderVideoView()}
          </div>
          {isVideoListVisible && (
            <ResponsiveVideoList
              currentSiteUser={currentSiteUser}
              channel={channel}
              mainVideo={mainVideo}
              isRTL={isRTL}
              onPlayRequestedBi={onPlayRequestedBi}
              onItemClick={this.navigateToVideoView}
              subscribeOnParentScroll={this.subscribeOnParentScroll}
            />
          )}
        </div>
      );
    }
  },
);
